/**
 * Guided onboarding coach ("Activate your space") for the web beta chat.
 *
 * After a guest's first message receives a Sage reply, a small window appears
 * and walks the user through the 5 onboarding steps with windows anchored to
 * the real CTAs:
 *   1. Connect your wallet (header Connect button)
 *   2. Make sure your wallet is on Ethereum Mainnet, then approve the connection
 *   3. Sign the verification message + create your Safe ID Token
 *   4. Have your first conversation with Sage
 *   5. Save your conversations with Sage
 *
 * The coach never blocks interaction, auto-advances as the user genuinely
 * completes each step (connect → sign → DID exists → first reply → save) and
 * only stops auto-appearing permanently once the first session is saved
 * (`onboardingStore.markSaveCompleted`), per product decision.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { X, ChevronRight, Wallet, KeyRound, Sparkles, MessageCircle, Save, ExternalLink } from 'lucide-react';
import { useWallet } from '../contexts/WalletContext';
import { useAuth } from '../contexts/AuthContext';
import { subscribeChatState } from '../utils/chatStateEvents';
import { markStepDismissed, shouldShowOnboarding } from '../utils/onboardingStore';
import {
  trackOnboardingStarted,
  trackOnboardingStepViewed,
  trackOnboardingCompleted,
  trackOnboardingDismissed,
} from '../lib/analytics';
import { useToast } from '../hooks/useToast';

const TOTAL_STEPS = 5;
const METAMASK_URL = 'https://metamask.io/';

interface StepDef {
  icon: React.ElementType;
  title: string;
  body: string;
  /** Anchor element selector to highlight, if any. */
  anchor?: string;
  footer?: { label: string; href: string };
}

const STEPS: StepDef[] = [
  {
    icon: Wallet,
    title: 'Connect your wallet',
    body: 'To benefit from all the features, connect your wallet.',
    anchor: '[data-onboarding-anchor="connect-wallet"]',
    footer: {
      label:
        "If you don't have a wallet, go to https://metamask.io/, click on get started and follow the instructions to create one.",
      href: METAMASK_URL,
    },
  },
  {
    icon: KeyRound,
    title: 'Approve the connection',
    body: 'Make sure your wallet is on Ethereum Mainnet, then approve the connection.',
    anchor: '[data-onboarding-anchor="sign-in"]',
  },
  {
    icon: Sparkles,
    title: 'Sign & create your Safe ID Token',
    body: 'Sign the verification message — this proves you own the address (no transaction fee). Then create your Safe ID Token to mint your soulbound identity on-chain. You only pay the transaction fees (make sure you have ETH in your wallet for the transaction). You’re ready as soon as you see "Signed In" at the top of the page.',
    anchor: '[data-onboarding-anchor="create-did"]',
  },
  {
    icon: MessageCircle,
    title: 'Have your first conversation',
    body: 'Open the chat and send a message. As soon as Sage replies, you’re fully activated: identity + first conversation.',
    anchor: '[data-onboarding-anchor="chat-input"]',
  },
  {
    icon: Save,
    title: 'Save your conversations (optional)',
    body: 'At the end of an exchange, keep your chat history if you wish by clicking "Save session with ID token"; you only pay the transaction fees.',
    anchor: '[data-onboarding-anchor="save-session"]',
  },
];

/**
 * The current "real" step derives from actual state, so a partially-onboarded
 * user always resumes at the first step they have not completed yet:
 *   not connected → 1 · connected → 2 · verified → 3 · DID exists → 4 · reply → 5
 */
function computeCurrentStep(input: {
  walletConnected: boolean;
  isVerified: boolean;
  didExists: boolean;
  hasFirstReply: boolean;
}): number {
  if (!input.isVerified) {
    return input.walletConnected ? 2 : 1;
  }
  if (!input.didExists) {
    return 3;
  }
  return input.hasFirstReply ? 5 : 4;
}

export default function OnboardingTour() {
  const { wallet } = useWallet();
  const { authState } = useAuth();
  const { showToast, ToastContainer } = useToast();

  const startedRef = useRef(false);
  const [enabled, setEnabled] = useState(false);
  const [step, setStep] = useState(1);
  const [hasFirstReply, setHasFirstReply] = useState(false);

  const isVerified = authState.isVerified;
  const didExists = authState.didStatus === 'exists';
  const walletConnected = Boolean(wallet?.address);

  /**
   * Chat-state signals: the first Sage answer is the tour trigger (the user
   * requested "after a user chatted with Sage and received one answer"), and a
   * confirmed first save permanently completes the tour.
   */
  useEffect(() => {
    const unsubscribe = subscribeChatState((event) => {
      if (event.firstReplyReceived) {
        setHasFirstReply(true);
        // Trigger on the first reply, unless the user already finished or
        // dismissed the tour this session.
        if (startedRef.current) return;
        startedRef.current = true;
        if (!shouldShowOnboarding()) return;
        setEnabled(true);
        void trackOnboardingStarted().catch(() => {});
      }
      if (event.saveConfirmed) {
        // Tour is only truly "completed" (stops auto-appearing) on a real save.
        setEnabled(false);
        void trackOnboardingCompleted().catch(() => {});
        showToast('success', 'You’re all set — your space with Sage is now secure and private.', 4200);
      }
    });
    return unsubscribe;
  }, [showToast]);

  /**
   * Manual re-open: the persistent "Tutorial" button (Footer, mirroring Cookie
   * Preferences) dispatches `open-onboarding-tour`. This bypasses every gate —
   * a user who skipped or even completed the first save can re-walk the tour.
   */
  useEffect(() => {
    const onOpen = () => {
      startedRef.current = true;
      setEnabled(true);
      void trackOnboardingStarted().catch(() => {});
    };
    window.addEventListener('open-onboarding-tour', onOpen);
    return () => window.removeEventListener('open-onboarding-tour', onOpen);
  }, []);

  // Keep `step` in sync with the real state, and track viewed steps.
  useEffect(() => {
    if (!enabled) return;
    const next = computeCurrentStep({
      walletConnected,
      isVerified,
      didExists,
      hasFirstReply,
    });
    setStep((prev) => {
      if (prev !== next) {
        void trackOnboardingStepViewed(next).catch(() => {});
      }
      return next;
    });
  }, [enabled, walletConnected, isVerified, didExists, hasFirstReply]);

  // Returning-user resume: a user who already connected + signed + minted but
  // never saved would not get a fresh first-reply event on reload, so resume
  // at the final "save your conversations" step once per browser session.
  // Skips anyone who already completed the whole flow or finished first save.
  useEffect(() => {
    if (startedRef.current) return;
    if (!shouldShowOnboarding()) return;
    if (!(isVerified && didExists)) return; // only relevant for activated users
    // Session-gate so the nudge isn't re-shown after each dismiss in a session.
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('sp_onboarding_resume') === '1') return;
    const t = setTimeout(() => {
      if (startedRef.current) return;
      startedRef.current = true;
      if (typeof sessionStorage !== 'undefined') sessionStorage.setItem('sp_onboarding_resume', '1');
      setHasFirstReply(true);
      setEnabled(true);
      void trackOnboardingStarted().catch(() => {});
      void trackOnboardingStepViewed(5).catch(() => {});
    }, 1200);
    return () => clearTimeout(t);
  }, [isVerified, didExists]);

  const dismiss = useCallback(() => {
    setEnabled(false);
    markStepDismissed(step);
    void trackOnboardingDismissed(step).catch(() => {});
  }, [step]);

  // Escape dismisses the coach (matches the app-wide Escape-to-blur handler).
  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, dismiss]);

  /**
   * Manual navigation (Next/Skip/Got it). Moving past the save step dismisses
   * — the tour is only truly "completed" (permanent) when a save is confirmed.
   */
  const goToStep = useCallback((next: number) => {
    if (next > TOTAL_STEPS) {
      setEnabled(false);
      markStepDismissed(TOTAL_STEPS);
      return;
    }
    setStep(next);
    void trackOnboardingStepViewed(next).catch(() => {});
  }, []);

  // Anchor highlight measurement.
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const [placement, setPlacement] = useState<'bottom' | 'overlay'>('bottom');

  const measure = useCallback(() => {
    if (!enabled) {
      setAnchorRect(null);
      return;
    }
    const def = STEPS[step - 1];
    // Step 2 anchors the Sign-In button when present, else falls back to Connect.
    let selector = def.anchor;
    if (step === 2 && !document.querySelector('[data-onboarding-anchor="sign-in"]')) {
      selector = '[data-onboarding-anchor="connect-wallet"]';
    }
    const el = selector ? (document.querySelector(selector) as HTMLElement | null) : null;
    if (el) {
      const rect = el.getBoundingClientRect();
      setAnchorRect(rect);
      setPlacement(rect.bottom + 260 < window.innerHeight ? 'bottom' : 'overlay');
    } else {
      setAnchorRect(null);
      if (step === 2 && !document.querySelector('[data-onboarding-anchor="connect-wallet"]')) {
        setPlacement('overlay');
      }
    }
  }, [enabled, step]);

  useEffect(() => {
    measure();
    window.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [measure]);

  /**
   * Re-measure when the anchored CTA mounts or moves. The save button appears
   * ~3s after a Sage reply and the Create-DID button lives in a modal, so a
   * one-shot timer misses them; watch the DOM for the anchor element instead.
   */
  useEffect(() => {
    if (!enabled) return;
    let selector = STEPS[step - 1].anchor;
    // Step 2 anchors the Sign-In button when present, else falls back to Connect.
    if (step === 2 && !document.querySelector('[data-onboarding-anchor="sign-in"]')) {
      selector = '[data-onboarding-anchor="connect-wallet"]';
    }
    if (!selector) return;

    // Measure once the anchor exists (it may mount late), then stop watching.
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const schedule = (fn: () => void, ms: number) => {
      const id = setTimeout(() => {
        timers.delete(id);
        fn();
      }, ms);
      timers.add(id);
    };
    let mo: MutationObserver | undefined;
    const tryMeasure = () => {
      if (document.querySelector(selector)) {
        mo?.disconnect();
        schedule(measure, 120); // let its layout settle
      }
    };
    mo = new MutationObserver(tryMeasure);
    mo.observe(document.body, { subtree: true, childList: true });
    // Cover the already-mounted case and layout settle after view changes.
    schedule(tryMeasure, 200);
    return () => {
      mo?.disconnect();
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, [enabled, step, didExists, isVerified, measure]);

  if (!enabled) return null;

  const def = STEPS[step - 1];
  const Icon = def.icon;
  const showOverlay = !anchorRect || placement === 'overlay';

  return (
    <>
      {/* Highlight ring around the anchor CTA (never blocks interaction) */}
      {!showOverlay && anchorRect && (
        <div
          aria-hidden="true"
          data-testid="onboarding-highlight"
          className="fixed z-[64] pointer-events-none rounded-xl ring-2 ring-primary-500/80 ring-offset-2 ring-offset-white dark:ring-offset-neutral-950 highlight-ring"
          style={{
            top: anchorRect.top - 4,
            left: anchorRect.left - 4,
            width: anchorRect.width + 8,
            height: anchorRect.height + 8,
          }}
        />
      )}

      {/* Coach card */}
      <div
        className={`fixed z-[65] w-[min(21rem,calc(100vw-2rem))] rounded-2xl border border-neutral-200 dark:border-white/10 bg-white/95 dark:bg-neutral-950/95 backdrop-blur-md shadow-2xl fade-in ${
          showOverlay ? 'left-1/2 -translate-x-1/2 bottom-6' : ''
        }`}
        style={
          !showOverlay && anchorRect
            ? {
                top: anchorRect.bottom + 12,
                left: Math.min(Math.max(anchorRect.left, 12), window.innerWidth - 352),
              }
            : undefined
        }
        role="dialog"
        aria-label={`Onboarding step ${step} of ${TOTAL_STEPS}`}
      >
        <div className="p-4 sm:p-5">
          {/* Header row */}
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-600 to-secondary-600 flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-white" />
              </span>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-body opacity-70">
                  Step {step} of {TOTAL_STEPS}
                </p>
                <h3 className="text-sm sm:text-base font-semibold text-heading leading-tight">{def.title}</h3>
              </div>
            </div>
            <button
              onClick={dismiss}
              className="p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center"
              aria-label="Not now — dismiss tour"
            >
              <X className="w-4 h-4 text-body opacity-70" />
            </button>
          </div>

          {/* Body */}
          <p className="text-[13px] sm:text-sm text-body leading-relaxed">{def.body}</p>

          {/* CTA link */}
          {def.footer && (
            <a
              href={def.footer.href}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-start gap-1.5 text-[12px] text-primary-700 dark:text-primary-300 hover:underline leading-snug"
            >
              <ExternalLink className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{def.footer.label}</span>
            </a>
          )}

          {/* Progress dots */}
          <div className="mt-4 flex items-center gap-1.5">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
              const n = i + 1;
              const done = n < step;
              const current = n === step;
              return (
                <span
                  key={n}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    current
                      ? 'w-6 bg-gradient-to-r from-primary-600 to-secondary-600'
                      : done
                        ? 'w-3 bg-primary-400/70'
                        : 'w-3 bg-neutral-300 dark:bg-neutral-700'
                  }`}
                />
              );
            })}
            <span className="ml-auto text-[11px] text-body opacity-60">
              {step === TOTAL_STEPS ? 'Almost done' : `${step} of ${TOTAL_STEPS}`}
            </span>
          </div>

          {/* Footer actions */}
          <div className="mt-3 flex items-center justify-end gap-2">
            <button
              onClick={dismiss}
              className="px-3 py-2 rounded-lg text-[13px] font-medium text-body opacity-80 hover:opacity-100 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors min-h-[36px]"
            >
              Not now
            </button>
            {step === TOTAL_STEPS ? (
              <button
                onClick={() => goToStep(TOTAL_STEPS + 1)}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-[13px] font-semibold text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg hover:shadow-xl transition-all min-h-[36px]"
              >
                Got it
              </button>
            ) : (
              <>
                <button
                  onClick={() => goToStep(step + 1)}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-[13px] font-semibold text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors min-h-[36px]"
                >
                  Skip
                </button>
                <button
                  onClick={() => goToStep(step + 1)}
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-[13px] font-semibold text-white bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 shadow-lg hover:shadow-xl transition-all min-h-[36px]"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
}
